import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertTriangle } from 'lucide-react';

export const ScheduleWorkshopPrompt = ({ course, onSchedule, onClose }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h2 className="text-xl font-bold text-levelup-dark mb-2">{t('workshops.contentDownloaded')}</h2>
          <p className="text-gray-600 text-sm mb-2">{t('workshops.schedulePromptMessage')}</p>
          <p className="text-gray-500 text-xs mb-6">{t('workshops.schedulePromptHelp')}</p>
          <div className="space-y-3">
            <button onClick={onSchedule}
              className="w-full bg-levelup-accent text-levelup-darker py-3 rounded-lg font-semibold hover:bg-levelup-accent-hover"
              data-testid="schedule-now-btn">
              {t('workshops.addScheduleNow')}
            </button>
            <button onClick={onClose}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200"
              data-testid="schedule-later-btn">
              {t('workshops.addScheduleLater')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MissingScheduleModal = ({ courses, onSchedule, onClose }) => {
  const { t } = useTranslation();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden" data-testid="missing-schedule-modal">
        <div className="bg-levelup-dark p-6 text-white">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="text-yellow-400" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">{t('missingSchedule.title')}</h2>
              <p className="text-gray-300 text-sm">{t('missingSchedule.subtitle')}</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <p className="text-gray-600 mb-4">{t('missingSchedule.message', { count: courses.length })}</p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-48 overflow-y-auto">
            <p className="text-sm font-medium text-gray-700 mb-3">{t('missingSchedule.coursesDownloaded')}:</p>
            <ul className="space-y-2">
              {courses.map((course) => (
                <li key={course.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-800 font-medium truncate flex-1 mr-2">{course.title}</span>
                  <span className="text-gray-500 text-xs whitespace-nowrap">{formatDate(course.last_downloaded)}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-sm text-gray-500 mb-6">{t('missingSchedule.helpText')}</p>
          <div className="flex space-x-3">
            <button onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              data-testid="missing-schedule-later-btn">
              {t('missingSchedule.later')}
            </button>
            <button onClick={onSchedule}
              className="flex-1 bg-levelup-accent text-levelup-darker py-3 rounded-lg font-semibold hover:bg-levelup-accent-hover transition-colors"
              data-testid="missing-schedule-now-btn">
              {t('missingSchedule.scheduleNow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
